import { useState, useMemo } from 'react'
import { format, parseISO } from 'date-fns'
import { useAuth } from '../../../hooks/useAuth'
import { useLinkedHouseholds } from '../../linkedHouseholds/hooks/useLinkedHouseholds'
import { useCustodyExpenses, useCustodyPayments, useCustodyBalance } from '../hooks/useCustody'
import { AddExpenseForm } from './AddExpenseForm'
import { RecordPaymentModal } from './RecordPaymentModal'
import { SkeletonList } from '../../../components/ui/Skeleton'
import { cn } from '../../../lib/utils'

const STATUS_CONFIG = {
  pending:      { label: 'Pending',      cls: 'bg-amber-100 text-amber-700' },
  acknowledged: { label: 'Acknowledged', cls: 'bg-sky-100 text-sky-700' },
  settled:      { label: 'Settled',      cls: 'bg-emerald-100 text-emerald-700' },
}

const CATEGORY_LABELS = {
  medical:'Medical', dental:'Dental', school:'School', activities:'Activities',
  clothing:'Clothing', childcare:'Childcare', travel:'Travel', other:'Other',
}

const ExpenseCard = ({ expense, ourHouseholdId, linkedHouseholdName, onAcknowledge, onEdit, onDelete }) => {
  const weePaid    = expense.household_id === ourHouseholdId
  const owedAmt    = expense.split_type === 'fifty_fifty'
    ? expense.amount * 0.5
    : expense.amount * (expense.split_percentage ?? 50) / 100

  const statusCfg  = STATUS_CONFIG[expense.status] ?? STATUS_CONFIG.pending
  const [confirm, setConfirm] = useState(false)

  return (
    <div className="bg-surface rounded-xl border border-border px-4 py-4 space-y-2">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-primary-light text-primary">
              {expense.child?.display_name}
            </span>
            <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-background text-text-secondary">
              {CATEGORY_LABELS[expense.category] ?? expense.category}
            </span>
          </div>
          <p className="text-sm font-semibold text-text-primary">{expense.description}</p>
          <p className="text-xs text-text-muted mt-0.5">
            Paid by {weePaid ? 'you' : linkedHouseholdName} · ${parseFloat(expense.amount).toFixed(2)}
          </p>
        </div>
        <span className={cn('text-xs font-semibold px-2 py-0.5 rounded-full flex-shrink-0', statusCfg.cls)}>
          {statusCfg.label}
        </span>
      </div>

      <div className="text-xs text-text-secondary bg-background rounded-lg px-3 py-2">
        {weePaid
          ? `${linkedHouseholdName} owes $${owedAmt.toFixed(2)} (${expense.split_type === 'fifty_fifty' ? '50' : expense.split_percentage}% of $${parseFloat(expense.amount).toFixed(2)})`
          : `You owe $${owedAmt.toFixed(2)} (${expense.split_type === 'fifty_fifty' ? '50' : expense.split_percentage}% of $${parseFloat(expense.amount).toFixed(2)})`
        }
      </div>

      <div className="flex items-center justify-between">
        <span className="text-xs text-text-muted">{format(parseISO(expense.expense_date), 'MMM d, yyyy')}</span>
        <div className="flex gap-2">
          {!weePaid && expense.status === 'pending' && (
            <button
              type="button"
              onClick={() => onAcknowledge(expense.id)}
              className="text-xs font-semibold text-sky-600 bg-sky-50 px-3 py-1 rounded-full"
            >
              Acknowledge
            </button>
          )}
          {weePaid && (
            <>
              {confirm ? (
                <>
                  <button type="button" onClick={() => setConfirm(false)}
                    className="text-xs text-text-muted">Cancel</button>
                  <button type="button" onClick={() => onDelete(expense.id)}
                    className="text-xs font-semibold text-rose-600">Delete</button>
                </>
              ) : (
                <>
                  <button type="button" onClick={() => onEdit(expense)}
                    className="text-xs text-text-secondary">Edit</button>
                  <button type="button" onClick={() => setConfirm(true)}
                    className="text-xs text-rose-500">Delete</button>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  )
}

const BalanceCard = ({ balance, linkedHouseholdName }) => {
  const net  = parseFloat(balance?.net_balance ?? 0)
  const abs  = Math.abs(net).toFixed(2)
  const owed = net > 0.01
  const even = Math.abs(net) < 0.01

  return (
    <div className={cn(
      'rounded-xl px-5 py-5 mb-4',
      even  ? 'bg-border/30' :
      owed  ? 'bg-emerald-50 border border-emerald-200' :
              'bg-rose-50 border border-rose-200',
    )}>
      <p className={cn(
        'text-2xl font-bold',
        even ? 'text-text-primary' : owed ? 'text-emerald-700' : 'text-rose-700',
      )}>
        {even ? 'All settled up' : owed ? `You are owed $${abs}` : `You owe $${abs}`}
      </p>
      {!even && (
        <p className="text-sm mt-1 opacity-70">
          {owed
            ? `${linkedHouseholdName} owes you`
            : `You owe ${linkedHouseholdName}`}
          {' · '}{balance?.unsettled_expense_count ?? 0} unsettled expense{balance?.unsettled_expense_count !== 1 ? 's' : ''}
        </p>
      )}
    </div>
  )
}

const TABS = ['All', 'Pending', 'Acknowledged', 'Settled', 'Payments']

export const ExpensesTab = () => {
  const { household, isAdmin }      = useAuth()
  const { activeLink }              = useLinkedHouseholds()
  const { data: expenses = [], isLoading: expLoading, acknowledge, remove } = useCustodyExpenses()
  const { data: payments = [], isLoading: payLoading } = useCustodyPayments()
  const { data: balance }           = useCustodyBalance()

  const linkedHouseholdName = activeLink?.otherHousehold?.name ?? 'Other House'

  const [activeTab,    setActiveTab]    = useState('All')
  const [showAddForm,  setShowAddForm]  = useState(false)
  const [editExpense,  setEditExpense]  = useState(null)
  const [showPayment,  setShowPayment]  = useState(false)

  const filtered = useMemo(() => {
    if (activeTab === 'All')          return expenses
    if (activeTab === 'Pending')      return expenses.filter((e) => e.status === 'pending')
    if (activeTab === 'Acknowledged') return expenses.filter((e) => e.status === 'acknowledged')
    if (activeTab === 'Settled')      return expenses.filter((e) => e.status === 'settled')
    return expenses
  }, [expenses, activeTab])

  // Group payments by month for history
  const paymentsByMonth = useMemo(() => {
    const groups = {}
    payments.forEach((p) => {
      const key = format(parseISO(p.payment_date), 'MMMM yyyy')
      if (!groups[key]) groups[key] = []
      groups[key].push(p)
    })
    return groups
  }, [payments])

  if (expLoading) return <div className="py-4"><SkeletonList count={3} /></div>

  return (
    <div className="space-y-4">
      <BalanceCard balance={balance} linkedHouseholdName={linkedHouseholdName} />

      {isAdmin && (
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => setShowPayment(true)}
            className="flex-1 border border-border rounded-xl py-2.5 text-sm font-medium text-text-secondary"
          >
            Record Payment
          </button>
          <button
            type="button"
            onClick={() => setShowAddForm(true)}
            className="flex-1 bg-primary text-white rounded-xl py-2.5 text-sm font-semibold"
          >
            Add Expense
          </button>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 overflow-x-auto pb-1">
        {TABS.map((tab) => (
          <button
            key={tab}
            type="button"
            onClick={() => setActiveTab(tab)}
            className={cn(
              'px-3 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-colors flex-shrink-0',
              activeTab === tab ? 'bg-primary text-white' : 'bg-surface border border-border text-text-secondary',
            )}
          >
            {tab}
          </button>
        ))}
      </div>

      {activeTab === 'Payments' ? (
        payLoading ? <SkeletonList count={2} /> :
        payments.length === 0 ? (
          <p className="text-sm text-text-muted text-center py-8">No payments yet.</p>
        ) : (
          Object.entries(paymentsByMonth).map(([month, monthPayments]) => (
            <div key={month}>
              <p className="text-xs font-semibold text-text-muted uppercase tracking-wide mb-2">{month}</p>
              <div className="space-y-2">
                {monthPayments.map((p) => (
                  <div key={p.id} className="bg-surface rounded-xl border border-border px-4 py-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-semibold text-text-primary">
                          ${parseFloat(p.amount).toFixed(2)}
                          {p.payment_method && ` · ${p.payment_method}`}
                        </p>
                        {p.reference_note && <p className="text-xs text-text-muted">{p.reference_note}</p>}
                      </div>
                      <span className="text-xs text-text-muted">{format(parseISO(p.payment_date), 'MMM d')}</span>
                    </div>
                    {p.expense_ids?.length > 0 && (
                      <p className="text-xs text-text-muted mt-1">Covers {p.expense_ids.length} expense{p.expense_ids.length !== 1 ? 's' : ''}</p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))
        )
      ) : (
        filtered.length === 0 ? (
          <p className="text-sm text-text-muted text-center py-8">
            {activeTab === 'All' ? 'No shared expenses yet.' : `No ${activeTab.toLowerCase()} expenses.`}
          </p>
        ) : (
          <div className="space-y-3">
            {filtered.map((e) => (
              <ExpenseCard
                key={e.id}
                expense={e}
                ourHouseholdId={household?.id}
                linkedHouseholdName={linkedHouseholdName}
                onAcknowledge={(id) => acknowledge.mutate(id)}
                onEdit={setEditExpense}
                onDelete={(id) => remove.mutate(id)}
              />
            ))}
          </div>
        )
      )}

      {(showAddForm || editExpense) && (
        <AddExpenseForm
          expense={editExpense}
          onClose={() => { setShowAddForm(false); setEditExpense(null) }}
        />
      )}
      {showPayment && (
        <RecordPaymentModal
          netBalance={balance?.net_balance}
          onClose={() => setShowPayment(false)}
        />
      )}
    </div>
  )
}
